import { useEffect, useRef, useState } from "react";
import { Phone, PhoneOff, Mic, MicOff, Video, VideoOff, MonitorUp, ShieldCheck, PhoneIncoming, Circle, Square, Image as ImageIcon, Volume2, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { useCall } from "@/context/CallContext";
import { BACKGROUNDS } from "@/lib/virtualBackground";
import { getRingtone } from "@/lib/ringtones";

function Avatar({ name, pic, size = "w-24 h-24 text-3xl" }) {
  if (pic) {
    return (
      <img src={pic} alt={name || "caller"} data-testid="caller-photo"
        className={`${size} rounded-full object-cover border-2 border-white/20`} />
    );
  }
  return (
    <div className={`${size} rounded-full bg-pula-green/20 grid place-items-center font-heading font-extrabold text-white/90`}>
      {name?.[0]?.toUpperCase() || "?"}
    </div>
  );
}

// Best-effort in-browser ringtone using WebAudio (no asset needed).
// Warm two-tone blend with a classic phone cadence: "brring-brring … pause" for an
// incoming call, a single long ringback for the caller — so it feels like a real phone.
function startRing(ref, incoming) {
  stopRing(ref); // never stack two ring instances — a leaked one would keep ringing during the call
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const gain = ctx.createGain();
    gain.gain.value = 0;
    gain.connect(ctx.destination);
    // Callee's incoming ring honours the member's chosen ringtone; caller ringback stays fixed.
    const tone = incoming ? getRingtone() : null;
    const oscType = tone ? tone.type : "sine";
    const f1 = tone ? tone.freqs[0] : 440;
    const f2 = tone ? tone.freqs[1] : 480;
    const osc1 = ctx.createOscillator(); osc1.type = oscType; osc1.frequency.value = f1; osc1.connect(gain); osc1.start();
    const osc2 = ctx.createOscillator(); osc2.type = oscType; osc2.frequency.value = f2; osc2.connect(gain); osc2.start();
    // [on?, ms] segments looped: chosen ringtone cadence for callee, single ringback for caller.
    const segments = incoming ? tone.segments : [[true, 1000], [false, 3000]];
    const holder = { ctx, osc1, osc2, gain, timer: null };
    ref.current = holder;
    let i = 0;
    const run = () => {
      const [on, ms] = segments[i % segments.length];
      gain.gain.setTargetAtTime(on ? 0.16 : 0, ctx.currentTime, 0.015);
      i += 1;
      holder.timer = setTimeout(run, ms);
    };
    run();
  } catch { /* audio blocked until user gesture — visual + vibration still work */ }
}
function stopRing(ref) {
  const r = ref.current;
  if (!r) return;
  try { if (r.timer) clearTimeout(r.timer); r.osc1.stop(); r.osc2.stop(); r.ctx.close(); } catch { /* ignore */ }
  ref.current = null;
}

// Soft two-note chime for an in-call chat message that arrives while you're not looking at the panel.
function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const g = ctx.createGain();
    g.gain.value = 0.0001;
    g.connect(ctx.destination);
    const o = ctx.createOscillator();
    o.type = "sine";
    o.connect(g);
    o.start();
    const t = ctx.currentTime;
    o.frequency.setValueAtTime(660, t);
    g.gain.exponentialRampToValueAtTime(0.12, t + 0.02);
    o.frequency.setValueAtTime(880, t + 0.09);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
    o.stop(t + 0.32);
    setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 500);
  } catch { /* ignore */ }
}

export function CallOverlay() {
  const {
    status, peer, media, muted, cameraOff, sharing, recording, bgMode,
    localStream, remoteStream, wakeup, callDiag, reconnecting, callQuality, chatMessages, peerTyping,
    acceptCall, rejectCall, endCall, toggleMute, toggleCamera, shareScreen, toggleRecording, setBackground,
    answerWakeup, dismissWakeup, sendChat, sendTyping, markChatSeen,
  } = useCall();
  const [bgOpen, setBgOpen] = useState(false);
  const [needTap, setNeedTap] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatDraft, setChatDraft] = useState("");
  const [chatSeen, setChatSeen] = useState(0);
  const chatEndRef = useRef(null);
  const lastChatCountRef = useRef(0);
  const typingSendRef = useRef(null);

  const localRef = useRef(null);
  const remoteRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const ringRef = useRef(null);
  const boostRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const [boostLevel, setBoostLevel] = useState(() => {
    // Reliability first: play the other person's audio through the plain <audio> element at 100%.
    // A previous build defaulted to a >100% WebAudio amplifier that produced SILENCE on some
    // Android/Chrome phones (connected but couldn't hear each other). Reset that saved preference
    // once so everyone recovers; louder-than-100% is now strictly opt-in.
    if (localStorage.getItem("pula_call_boost_v3") !== "1") {
      localStorage.setItem("pula_call_boost_v3", "1");
      localStorage.setItem("pula_call_boost", "1");
      return 1;
    }
    const v = parseFloat(localStorage.getItem("pula_call_boost") || "1");
    return isNaN(v) ? 1 : Math.min(4, Math.max(1, v));
  });
  const [volOpen, setVolOpen] = useState(false);

  // Engage the WebAudio amplifier ONLY when the user asks for >100% (the fragile path). At 100%
  // we never touch WebAudio — the element plays directly, which is rock-solid on every device.
  const engageBoost = (level) => {
    try {
      const stream = remoteStreamRef.current;
      const el = remoteAudioRef.current;
      if (boostRef.current || !stream || !el) return;
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) { revertBoost("This phone can't boost the volume — kept at 100%."); return; }
      const ctx = new AC();
      const source = ctx.createMediaStreamSource(stream);
      const gain = ctx.createGain();
      gain.gain.value = Math.max(1, level || boostLevel);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(gain);
      gain.connect(analyser);
      gain.connect(ctx.destination);
      boostRef.current = { ctx, source, gain, analyser };
      // Route through WebAudio only while the context is truly running; otherwise fall back to the
      // element so audio is NEVER lost when the browser suspends the context.
      const applyMute = () => { if (remoteAudioRef.current) remoteAudioRef.current.muted = (ctx.state === "running"); };
      ctx.onstatechange = applyMute;
      ctx.resume?.().then(applyMute).catch(applyMute);
      applyMute();
      verifyBoost(analyser); // auto-revert if this device's WebAudio path turns out silent
    } catch { revertBoost(); }
  };
  // Some Android/Chrome phones produce SILENCE when a live call stream is routed through WebAudio.
  // We only JUDGE the boost while its AudioContext is actually 'running' (i.e. truly carrying the
  // audio — the element is muted then). If the context hasn't resumed yet, the element is still
  // unmuted and audible, so we just wait. We latch on the first real sound; we only revert if the
  // running WebAudio path stays perfectly flat for 3.5s of running-time (a genuinely dead device).
  const verifyBoost = (analyser) => {
    const buf = new Uint8Array(analyser.fftSize);
    const start = Date.now();
    let runningSince = 0;
    const tick = () => {
      const b = boostRef.current;
      if (!b || b.analyser !== analyser) return;
      if (b.ctx.state === "running") {
        if (!runningSince) runningSince = Date.now();
        analyser.getByteTimeDomainData(buf);
        for (let i = 0; i < buf.length; i++) { if (Math.abs(buf[i] - 128) > 1) return; } // real sound → keep boost (latched)
        if (Date.now() - runningSince >= 3500) { revertBoost("Extra loudness isn't supported on this phone — kept at 100% so you can hear."); return; }
      }
      if (Date.now() - start < 15000) setTimeout(tick, 150); // stop probing after 15s; audio stays fine either way
    };
    setTimeout(tick, 150);
  };
  const disengageBoost = () => {
    const b = boostRef.current;
    boostRef.current = null;
    if (remoteAudioRef.current) { remoteAudioRef.current.muted = false; remoteAudioRef.current.volume = 1; }
    if (!b) return;
    try { b.source.disconnect(); b.gain.disconnect(); b.analyser?.disconnect(); b.ctx.close?.(); } catch { /* ignore */ }
    // Re-attach the stream so the element resumes normal output after leaving the WebAudio graph.
    try { if (remoteAudioRef.current && remoteStreamRef.current) { remoteAudioRef.current.srcObject = remoteStreamRef.current; remoteAudioRef.current.play?.().catch(() => {}); } } catch { /* ignore */ }
  };
  const revertBoost = (msg) => {
    disengageBoost();
    setBoostLevel(1);
    localStorage.setItem("pula_call_boost", "1");
    if (msg) toast.message(msg);
  };

  const applyBoost = (level) => {
    const lvl = Math.min(4, Math.max(1, level));
    setBoostLevel(lvl);
    localStorage.setItem("pula_call_boost", String(lvl));
    if (lvl > 1) {
      if (boostRef.current?.gain) { try { boostRef.current.gain.gain.value = lvl; } catch { /* ignore */ } }
      else engageBoost(lvl);
    } else {
      disengageBoost(); // back to rock-solid element playback at 100%
    }
  };

  useEffect(() => {
    if (!remoteStream) return undefined;
    remoteStreamRef.current = remoteStream;
    if (boostLevel > 1) engageBoost(boostLevel); // only if the user opted into extra loudness
    return () => disengageBoost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteStream]);

  useEffect(() => { if (localRef.current && localStream) localRef.current.srcObject = localStream; }, [localStream, status]);
  useEffect(() => {
    if (!remoteStream) return;
    // Audio plays from a dedicated always-mounted <audio> element (never display:none), so voice
    // is heard even before the video surface appears / on voice-only calls. The <video> element
    // carries the picture and is muted to avoid double audio.
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = remoteStream;
      // Default path: always audible through the element unless the user opted into >100% boost.
      if (!boostRef.current) { remoteAudioRef.current.muted = false; remoteAudioRef.current.volume = 1; }
      const p = remoteAudioRef.current.play?.();
      if (p && p.then) p.then(() => setNeedTap(false)).catch(() => setNeedTap(true));
    }
    if (remoteRef.current) {
      remoteRef.current.srcObject = remoteStream;
      const vp = remoteRef.current.play?.();
      if (vp && vp.catch) vp.catch(() => {});
    }
  }, [remoteStream, status]);

  // Keep the chat scrolled to the newest line; count unread when the panel is closed.
  useEffect(() => {
    if (chatOpen) {
      setChatSeen(chatMessages.length);
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      if (chatMessages.some((m) => !m.mine)) markChatSeen(); // tell the sender we've read them
    }
  }, [chatMessages, chatOpen, markChatSeen]);
  const unreadChat = Math.max(0, chatMessages.length - chatSeen);
  const lastMineIdx = (() => { for (let i = chatMessages.length - 1; i >= 0; i--) { if (chatMessages[i].mine) return i; } return -1; })();

  // Soft chime + buzz when a message arrives while you're not looking at the chat panel.
  useEffect(() => {
    const n = chatMessages.length;
    if (n > lastChatCountRef.current) {
      const last = chatMessages[n - 1];
      if (last && !last.mine && !chatOpen) {
        playChime();
        if (navigator.vibrate) { try { navigator.vibrate([120, 60, 120]); } catch { /* ignore */ } }
      }
    }
    lastChatCountRef.current = n;
  }, [chatMessages, chatOpen]);

  const onChatInput = (e) => {
    setChatDraft(e.target.value);
    sendTyping(true);
    if (typingSendRef.current) clearTimeout(typingSendRef.current);
    typingSendRef.current = setTimeout(() => sendTyping(false), 1800);
  };

  const submitChat = (e) => {
    e?.preventDefault?.();
    const t = chatDraft.trim();
    if (!t) return;
    sendChat(t);
    setChatDraft("");
    if (typingSendRef.current) clearTimeout(typingSendRef.current);
    sendTyping(false);
  };


  const tapToPlay = () => {    try { boostRef.current?.ctx?.resume?.().then(() => { if (boostRef.current?.ctx?.state === "running" && remoteAudioRef.current) remoteAudioRef.current.muted = true; }).catch(() => {}); } catch { /* ignore */ }
    try { const p = remoteAudioRef.current?.play?.(); if (p && p.then) p.then(() => setNeedTap(false)).catch(() => {}); } catch { /* ignore */ }
    try { remoteRef.current?.play?.(); } catch { /* ignore */ }
    setNeedTap(false);
  };

  // Ringtone (callee) + dial/ringback tone (caller) + vibration while ringing.
  useEffect(() => {
    const incoming = status === "incoming";
    const alerting = incoming || !!wakeup;
    const ringing = alerting || status === "calling";
    if (!ringing) { stopRing(ringRef); return undefined; }
    startRing(ringRef, alerting);
    let vib;
    if (alerting && navigator.vibrate) {
      try { navigator.vibrate([400, 200, 400]); vib = setInterval(() => navigator.vibrate([400, 200, 400]), 1600); } catch { /* ignore */ }
    }
    return () => {
      stopRing(ringRef);
      if (vib) clearInterval(vib);
      if (navigator.vibrate) { try { navigator.vibrate(0); } catch { /* ignore */ } }
    };
  }, [status, wakeup]);

  // A call arrived while your app was closed — show it the moment you open Pula so you can answer.
  if (status === "idle" && wakeup) {
    return (
      <div className="fixed inset-0 z-[100] bg-pula-ink/95 backdrop-blur grid place-items-center px-6" data-testid="wakeup-call">
        <div className="text-center text-white max-w-sm w-full">
          <PhoneIncoming className="w-10 h-10 mx-auto text-pula-highlight animate-pulse" />
          <p className="mt-4 text-sm text-white/60 font-body uppercase tracking-wide">
            {wakeup.name} is calling you
          </p>
          <div className="mt-4 flex flex-col items-center gap-3">
            <Avatar name={wakeup.name} pic={wakeup.pic} />
            <p className="font-heading font-bold text-2xl">{wakeup.name}</p>
            <p className="text-sm text-white/60 font-body">Called while you were away — answer now to connect and call them right back.</p>
          </div>
          <div className="mt-8 flex items-center justify-center gap-6">
            <button onClick={dismissWakeup} data-testid="wakeup-dismiss" className="w-16 h-16 rounded-full bg-red-500 grid place-items-center">
              <PhoneOff className="w-7 h-7 text-white" />
            </button>
            <button onClick={answerWakeup} data-testid="wakeup-answer" className="w-16 h-16 rounded-full bg-pula-green grid place-items-center animate-pulse">
              <Phone className="w-7 h-7 text-white" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === "idle") return null;

  // Incoming call prompt
  if (status === "incoming") {
    return (
      <div className="fixed inset-0 z-[100] bg-pula-ink/95 backdrop-blur grid place-items-center px-6" data-testid="incoming-call">
        <div className="text-center text-white max-w-sm w-full">
          <PhoneIncoming className="w-10 h-10 mx-auto text-pula-highlight animate-pulse" />
          <p className="mt-4 text-sm text-white/60 font-body uppercase tracking-wide">
            Incoming {media === "video" ? "video" : "voice"} call
          </p>
          <div className="mt-4 flex flex-col items-center gap-3">
            <Avatar name={peer?.name} pic={peer?.pic} />
            <p className="font-heading font-extrabold text-2xl">{peer?.name || "Someone"}</p>
          </div>
          <div className="mt-10 flex items-center justify-center gap-10">
            <button data-testid="call-reject-btn" onClick={rejectCall}
              className="flex flex-col items-center gap-2 group">
              <span className="w-16 h-16 rounded-full bg-pula-accent grid place-items-center group-hover:scale-105 transition-transform"><PhoneOff className="w-7 h-7 text-white" /></span>
              <span className="text-xs font-body text-white/70">Decline</span>
            </button>
            <button data-testid="call-accept-btn" onClick={acceptCall}
              className="flex flex-col items-center gap-2 group">
              <span className="w-16 h-16 rounded-full bg-pula-green grid place-items-center group-hover:scale-105 transition-transform animate-pulse"><Phone className="w-7 h-7 text-white" /></span>
              <span className="text-xs font-body text-white/70">Accept</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  const showRemoteVideo = media === "video" && remoteStream && status === "connected";

  return (
    <div className="fixed inset-0 z-[100] bg-pula-ink text-white flex flex-col" data-testid="call-overlay">
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between bg-black/30">
        <div>
          <p className="font-heading font-bold text-lg">{peer?.name || "Call"}</p>
          <p className="text-xs text-white/60 font-body flex items-center gap-1.5">
            {status === "connected" && callQuality && (
              <span
                data-testid="call-quality-dot"
                title={
                  callQuality.level === "weak" ? "Weak network — audio may stutter"
                  : callQuality.level === "fair" ? "Fair network" : "Strong network"
                }
                className={`inline-block w-2.5 h-2.5 rounded-full ${
                  callQuality.level === "weak" ? "bg-red-500 animate-pulse"
                  : callQuality.level === "fair" ? "bg-amber-400" : "bg-pula-green"
                }`}
              />
            )}
            {reconnecting ? <span className="text-pula-highlight font-semibold animate-pulse">Reconnecting…</span>
              : status === "calling" ? "Calling…"
              : status === "connected" ? (callQuality?.level === "weak" ? "Weak signal" : "Connected")
              : "Connecting…"}
            {media !== "video" && <span>· Voice only</span>}
            {recording && <span className="flex items-center gap-1 text-pula-accent font-semibold" data-testid="call-rec-indicator"><Circle className="w-2.5 h-2.5 fill-current animate-pulse" /> REC</span>}
          </p>
        </div>
        <span className="text-xs text-white/50 font-body flex items-center gap-1.5" data-testid="call-encryption-note">
          <ShieldCheck className="w-3.5 h-3.5 text-pula-green" /> Encrypted · not stored
        </span>
      </div>

      {/* Stage */}
      <div className="relative flex-1 grid place-items-center overflow-hidden">
        {/* Remote media stays mounted the whole call so audio is never torn down; we just
            show/hide the video surface. Audio comes from the dedicated <audio> element below,
            so voice keeps flowing even before the video connects and on voice-only calls. */}
        <video
          ref={remoteRef}
          data-testid="remote-video"
          autoPlay
          playsInline
          muted
          className={showRemoteVideo ? "w-full h-full object-cover" : "hidden"}
        />
        {remoteStream && <audio ref={remoteAudioRef} data-testid="remote-audio" autoPlay playsInline />}

        {!showRemoteVideo && (
          <div className="text-center">
            <Avatar name={peer?.name} pic={peer?.pic} size="w-28 h-28 text-4xl" />
            <p className="mt-4 font-body text-white/70">
              {status === "calling" ? "Ringing…" : media === "video" ? "Waiting for video…" : "Voice call in progress"}
            </p>
            {callDiag && (
              <div data-testid="call-diagnostic" className="mt-5 mx-auto max-w-xs text-left bg-black/50 border border-white/15 rounded-xl p-4 text-xs font-body text-white/80 space-y-1">
                <p className="font-heading font-bold text-white flex items-center gap-1.5">⚠️ Couldn't connect the media</p>
                <p>Reason: <b>{callDiag.reason}</b></p>
                <p>Relay (TURN) offered: <b>{callDiag.turnConfigured ? "yes" : "NO"}</b></p>
                <p>Relay path found: <b className={callDiag.relayFound ? "text-pula-green" : "text-red-300"}>{callDiag.relayFound ? "yes" : "NO"}</b></p>
                <p>Your network paths: <b>{callDiag.candidateTypes}</b></p>
                <p>ICE: <b>{callDiag.iceState}</b> · Conn: <b>{callDiag.connState}</b></p>
                <p>Relay errors: <b>{callDiag.iceErrors}</b>{callDiag.lastErr ? ` (${callDiag.lastErr})` : ""}</p>
                <p className="pt-1 text-white/60">📸 Please screenshot this and send it — it tells us exactly what to fix.</p>
              </div>
            )}
          </div>
        )}

        {/* Autoplay was blocked — a single tap (user gesture) starts audio + video. */}
        {needTap && remoteStream && (
          <button
            data-testid="tap-to-play"
            onClick={tapToPlay}
            className="absolute inset-0 z-10 grid place-items-center bg-black/70 backdrop-blur-sm">
            <span className="flex flex-col items-center gap-3 text-white">
              <Phone className="w-10 h-10 text-pula-green animate-pulse" />
              <span className="font-heading font-bold text-lg">Tap to join the call</span>
              <span className="text-xs text-white/70 font-body">Your phone needs one tap to start audio &amp; video</span>
            </span>
          </button>
        )}

        {/* Local preview */}
        {media === "video" && localStream && (
          <div className="absolute bottom-4 right-4 w-28 sm:w-40 aspect-[3/4] rounded-xl overflow-hidden border border-white/20 shadow-lg bg-black">
            <video ref={localRef} data-testid="local-video" autoPlay playsInline muted className="w-full h-full object-cover" />
            {cameraOff && <div className="absolute inset-0 grid place-items-center bg-black/70 text-xs text-white/70">Camera off</div>}
          </div>
        )}
      </div>

      {/* In-call chat — a quick text line for when the network gets weak */}
      {status === "connected" && chatOpen && (
        <div data-testid="call-chat-panel" className="absolute bottom-24 left-1/2 -translate-x-1/2 w-[92%] max-w-md bg-pula-ink/95 border border-white/15 rounded-2xl shadow-2xl z-20 flex flex-col overflow-hidden" style={{ maxHeight: "50vh" }}>
          <div className="px-4 py-2.5 border-b border-white/10 flex items-center justify-between">
            <span className="font-heading font-bold text-white text-sm flex items-center gap-2"><MessageCircle className="w-4 h-4 text-pula-green" /> Chat</span>
            <button data-testid="call-chat-close" onClick={() => setChatOpen(false)} className="text-white/50 hover:text-white text-xs font-body">Close</button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 min-h-[80px]">
            {chatMessages.length === 0 ? (
              <p className="text-white/40 text-xs font-body text-center py-4">Send a quick message if the audio drops out.</p>
            ) : chatMessages.map((m, i) => (
              <div key={i} data-testid="call-chat-message" className={`flex flex-col ${m.mine ? "items-end" : "items-start"}`}>
                <span className={`inline-block max-w-[80%] rounded-2xl px-3 py-1.5 text-sm font-body break-words ${m.mine ? "bg-pula-green text-white rounded-br-sm" : "bg-white/15 text-white rounded-bl-sm"}`}>{m.text}</span>
                {m.mine && i === lastMineIdx && m.seen && (
                  <span data-testid="call-chat-seen" className="text-[10px] text-white/45 font-body mt-0.5">Seen ✓✓</span>
                )}
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          {peerTyping && (
            <div data-testid="call-chat-typing" className="px-4 pb-1 flex items-center gap-1.5 text-white/50 text-xs font-body">
              <span className="flex gap-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-white/50 animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-white/50 animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-white/50 animate-bounce" style={{ animationDelay: "300ms" }} />
              </span>
              {peer?.name?.split(" ")[0] || "They"} is typing…
            </div>
          )}
          <form onSubmit={submitChat} className="p-2.5 border-t border-white/10 flex items-center gap-2">
            <input data-testid="call-chat-input" value={chatDraft} onChange={onChatInput}
              placeholder="Type a message…" autoComplete="off"
              className="flex-1 bg-white/10 text-white placeholder-white/40 rounded-full px-4 py-2 text-sm font-body outline-none focus:bg-white/15" />
            <button data-testid="call-chat-send" type="submit" disabled={!chatDraft.trim()}
              className="w-10 h-10 rounded-full bg-pula-green grid place-items-center text-white disabled:opacity-40 shrink-0">
              <Send className="w-5 h-5" />
            </button>
          </form>
        </div>
      )}

      {/* Controls */}
      <div className="px-4 py-5 bg-black/40 flex items-center justify-center gap-4 sm:gap-6">
        <button data-testid="call-mute-btn" onClick={toggleMute}
          className={`w-14 h-14 rounded-full grid place-items-center transition-colors ${muted ? "bg-white text-pula-ink" : "bg-white/15 hover:bg-white/25"}`}
          title={muted ? "Unmute" : "Mute"}>
          {muted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
        </button>

        {media === "video" && (
          <button data-testid="call-camera-btn" onClick={toggleCamera}
            className={`w-14 h-14 rounded-full grid place-items-center transition-colors ${cameraOff ? "bg-white text-pula-ink" : "bg-white/15 hover:bg-white/25"}`}
            title={cameraOff ? "Turn camera on" : "Turn camera off (save data)"}>
            {cameraOff ? <VideoOff className="w-6 h-6" /> : <Video className="w-6 h-6" />}
          </button>
        )}

        {media === "video" && status === "connected" && (
          <button data-testid="call-share-btn" onClick={shareScreen}
            className={`w-14 h-14 rounded-full grid place-items-center transition-colors ${sharing ? "bg-pula-green" : "bg-white/15 hover:bg-white/25"}`}
            title="Share screen">
            <MonitorUp className="w-6 h-6" />
          </button>
        )}

        {media === "video" && status === "connected" && (
          <div className="relative">
            <button data-testid="call-bg-btn" onClick={() => setBgOpen((v) => !v)}
              className={`w-14 h-14 rounded-full grid place-items-center transition-colors ${bgMode !== "none" ? "bg-pula-green" : "bg-white/15 hover:bg-white/25"}`}
              title="Virtual background">
              <ImageIcon className="w-6 h-6" />
            </button>
            {bgOpen && (
              <div data-testid="call-bg-picker" className="absolute bottom-16 left-1/2 -translate-x-1/2 bg-pula-ink/95 border border-white/15 rounded-2xl p-2 flex gap-2 shadow-xl">
                {BACKGROUNDS.map((b) => (
                  <button key={b.key} data-testid={`call-bg-option-${b.key}`} onClick={() => { setBackground(b.key); setBgOpen(false); }}
                    className={`w-14 h-14 rounded-xl overflow-hidden grid place-items-center text-[11px] font-body border-2 transition-colors ${bgMode === b.key ? "border-pula-green" : "border-transparent"} ${b.url ? "" : "bg-white/10"}`}
                    style={b.url ? { backgroundImage: `url(${b.url})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}
                    title={b.label}>
                    <span className={b.url ? "bg-black/40 w-full h-full grid place-items-center text-white" : "text-white"}>{b.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {status === "connected" && (
          <div className="relative">
            <button data-testid="call-volume-btn" onClick={() => setVolOpen((v) => !v)}
              className="w-14 h-14 rounded-full grid place-items-center transition-colors bg-white/15 hover:bg-white/25"
              title="Voice volume boost">
              <Volume2 className="w-6 h-6" />
            </button>
            {volOpen && (
              <div data-testid="call-volume-panel" className="absolute bottom-16 left-1/2 -translate-x-1/2 bg-pula-ink/95 border border-white/15 rounded-2xl p-4 shadow-xl w-56">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-white/80 font-body">Voice volume</span>
                  <span className="text-xs text-white font-bold">{Math.round(boostLevel * 100)}%</span>
                </div>
                <input data-testid="call-volume-slider" type="range" min="1" max="4" step="0.25" value={boostLevel}
                  onChange={(e) => applyBoost(parseFloat(e.target.value))}
                  className="w-full accent-pula-green" />
                <p className="text-[10px] text-white/50 font-body mt-1.5">100% works on every phone. Above 100% amplifies them — if you suddenly can't hear each other, drag back to 100%.</p>
              </div>
            )}
          </div>
        )}

        {status === "connected" && (
          <button data-testid="call-chat-btn" onClick={() => setChatOpen((v) => !v)}
            className={`relative w-14 h-14 rounded-full grid place-items-center transition-colors ${chatOpen ? "bg-pula-green" : "bg-white/15 hover:bg-white/25"}`}
            title="Chat during the call">
            <MessageCircle className="w-6 h-6" />
            {unreadChat > 0 && !chatOpen && (
              <span data-testid="call-chat-unread" className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 rounded-full bg-pula-accent text-white text-[11px] font-bold grid place-items-center">{unreadChat}</span>
            )}
          </button>
        )}

        {status === "connected" && (
          <button data-testid="call-record-btn" onClick={toggleRecording}
            className={`w-14 h-14 rounded-full grid place-items-center transition-colors ${recording ? "bg-pula-accent" : "bg-white/15 hover:bg-white/25"}`}
            title={recording ? "Stop recording (saves to your device)" : "Record (saved only to your device)"}>
            {recording ? <Square className="w-5 h-5 fill-current" /> : <Circle className="w-6 h-6" />}
          </button>
        )}

        <button data-testid="call-hangup-btn" onClick={endCall}
          className="w-16 h-14 rounded-full bg-pula-accent hover:bg-pula-accent/90 grid place-items-center transition-colors"
          title="End call">
          <PhoneOff className="w-7 h-7 text-white" />
        </button>
      </div>
    </div>
  );
}
